/**
 * Streak Calculation Utilities
 * 
 * A streak is defined as consecutive 7-day windows where a user makes at least one submission
 * of any type (elevator pitch, casual encounter, sales presentation) in standard or interactive mode.
 */

/**
 * Calculate the current streak for a user based on their assessment submissions
 * @param {Array} assessments - Array of assessment objects with completedDateTime
 * @returns {Object} - Object containing currentStreak, longestStreak, and streakHistory
 */
export const calculateUserStreaks = (assessments) => {
    // Filter for completed assessments and valid submission types
    const validSubmissionTypes = [
        'Elevator Pitch',
        'Casual Encounter',
        'Sales Presentation',
        'Sales Presentation / Demo',
        'Investor Pitch',
        'Objection Handling',
        'Document Defense',
        'Messaging Defense'
    ];

    const completedAssessments = assessments
        .filter(assessment => 
            assessment.completedDateTime && 
            validSubmissionTypes.includes(assessment.sessionType)
        )
        .sort((a, b) => new Date(a.completedDateTime) - new Date(b.completedDateTime));

    if (completedAssessments.length === 0) {
        return {
            currentStreak: 0,
            longestStreak: 0,
            streakHistory: [],
            lastSubmissionDate: null
        };
    }

    // Group submissions by 7-day windows
    const weeklyWindows = groupSubmissionsByWeeklyWindows(completedAssessments);
    
    // Calculate streaks from weekly windows
    const streaks = calculateStreaksFromWindows(weeklyWindows);
    
    // Get current streak (check if current week or recent weeks have submissions)
    const currentStreak = getCurrentStreakLength(weeklyWindows);
    
    // Get longest streak from history
    const longestStreak = Math.max(...streaks, 0);

    return {
        currentStreak,
        longestStreak,
        streakHistory: streaks,
        lastSubmissionDate: completedAssessments[completedAssessments.length - 1].completedDateTime,
        totalSubmissions: completedAssessments.length,
        weeklyWindows: weeklyWindows
    };
};

/**
 * Group submissions into 7-day windows starting from Monday
 * @param {Array} assessments - Sorted array of completed assessments
 * @returns {Map} - Map of week start dates to submission counts
 */
const groupSubmissionsByWeeklyWindows = (assessments) => {
    const weeklyWindows = new Map();

    assessments.forEach(assessment => {
        const submissionDate = new Date(assessment.completedDateTime);
        const weekStart = getWeekStart(submissionDate);
        const weekKey = weekStart.toISOString().split('T')[0]; // YYYY-MM-DD format

        if (!weeklyWindows.has(weekKey)) {
            weeklyWindows.set(weekKey, {
                weekStart: weekStart,
                submissions: []
            });
        }

        weeklyWindows.get(weekKey).submissions.push(assessment);
    });

    return weeklyWindows;
};

/**
 * Get the Monday of the week for a given date (UTC-based to avoid timezone issues)
 * @param {Date} date - The date to find the week start for
 * @returns {Date} - The Monday of that week at UTC midnight
 */
const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getUTCDay();
    const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
    d.setUTCDate(diff);
    d.setUTCHours(0, 0, 0, 0);
    return d;
};

/**
 * Calculate all streak lengths from weekly windows
 * @param {Map} weeklyWindows - Map of weekly submission windows
 * @returns {Array} - Array of streak lengths
 */
const calculateStreaksFromWindows = (weeklyWindows) => {
    const sortedWeeks = Array.from(weeklyWindows.keys()).sort();
    
    if (sortedWeeks.length === 0) return [0];

    const streaks = [];
    let currentStreakLength = 0;

    for (let i = 0; i < sortedWeeks.length; i++) {
        if (i === 0) {
            // First week always starts a streak
            currentStreakLength = 1;
        } else {
            // Compare using ISO date strings to avoid DST/timezone issues
            const prevDate = new Date(sortedWeeks[i - 1] + 'T00:00:00Z');
            const expectedNext = new Date(prevDate.getTime() + 7 * 24 * 60 * 60 * 1000);
            const expectedNextKey = expectedNext.toISOString().split('T')[0];

            if (sortedWeeks[i] === expectedNextKey) {
                // Consecutive week - continue streak
                currentStreakLength++;
            } else {
                // Gap found - end current streak and start new one
                streaks.push(currentStreakLength);
                currentStreakLength = 1;
            }
        }
    }

    // Add the final streak
    streaks.push(currentStreakLength);

    return streaks;
};

/**
 * Calculate current active streak length
 * @param {Map} weeklyWindows - Map of weekly submission windows
 * @returns {number} - Current streak length
 */
const getCurrentStreakLength = (weeklyWindows) => {
    const now = new Date();
    const currentWeekStart = getWeekStart(now);
    const currentWeekKey = currentWeekStart.toISOString().split('T')[0];
    
    // Check if there's a submission in the current week
    let checkWeekKey = currentWeekKey;
    let streakCount = 0;

    // Look backwards from current week to find consecutive weeks with submissions
    while (true) {
        if (weeklyWindows.has(checkWeekKey)) {
            streakCount++;
            // Calculate previous week using UTC milliseconds to avoid DST issues
            const checkDate = new Date(checkWeekKey + 'T00:00:00Z');
            const prevDate = new Date(checkDate.getTime() - 7 * 24 * 60 * 60 * 1000);
            checkWeekKey = prevDate.toISOString().split('T')[0];
        } else {
            // No submission found for this week - streak broken
            break;
        }
    }

    return streakCount;
};

/**
 * Check if a user is currently on a streak
 * @param {Array} assessments - Array of assessment objects
 * @returns {boolean} - True if user has submitted something in current or previous week
 */
export const isUserOnActiveStreak = (assessments) => {
    const { currentStreak } = calculateUserStreaks(assessments);
    return currentStreak > 0;
};

/**
 * Get streak status for display
 * @param {Array} assessments - Array of assessment objects
 * @returns {Object} - Streak status with display information
 */
export const getStreakStatus = (assessments) => {
    const streakData = calculateUserStreaks(assessments);
    
    return {
        ...streakData,
        isActive: streakData.currentStreak > 0,
        streakLevel: getStreakLevel(streakData.currentStreak),
        nextMilestone: getNextMilestone(streakData.currentStreak)
    };
};

/**
 * Get streak level badge (Bronze, Silver, Gold, etc.)
 * @param {number} streakLength - Current streak length
 * @returns {string} - Streak level name
 */
const getStreakLevel = (streakLength) => {
    if (streakLength >= 12) return 'Diamond'; // 3+ months
    if (streakLength >= 8) return 'Gold';     // 2+ months  
    if (streakLength >= 4) return 'Silver';   // 1+ month
    if (streakLength >= 2) return 'Bronze';   // 2+ weeks
    if (streakLength >= 1) return 'Starter';  // 1+ week
    return 'None';
};

/**
 * Get next milestone for motivation
 * @param {number} streakLength - Current streak length
 * @returns {Object} - Next milestone information
 */
const getNextMilestone = (streakLength) => {
    const milestones = [
        { weeks: 2, name: 'Bronze Streak', reward: '2 Week Streak' },
        { weeks: 4, name: 'Silver Streak', reward: '1 Month Streak' },
        { weeks: 8, name: 'Gold Streak', reward: '2 Month Streak' },
        { weeks: 12, name: 'Diamond Streak', reward: '3 Month Streak' },
        { weeks: 26, name: 'Legendary Streak', reward: '6 Month Streak' },
        { weeks: 52, name: 'Master Streak', reward: '1 Year Streak' }
    ];

    const nextMilestone = milestones.find(m => m.weeks > streakLength);
    
    if (nextMilestone) {
        return {
            target: nextMilestone.weeks,
            name: nextMilestone.name,
            reward: nextMilestone.reward,
            weeksToGo: nextMilestone.weeks - streakLength
        };
    }
    
    return null; // Already at max level
};

/**
 * Get weekly practice data for the last N weeks, broken down by session type
 * @param {Array} assessments - Array of assessment objects
 * @param {number} numberOfWeeks - Number of weeks to include (default 12)
 * @returns {Array} - Array of weekly data objects for charting
 */
export const getWeeklyPracticeData = (assessments, numberOfWeeks = 12) => {
    const validSubmissionTypes = [
        'Elevator Pitch',
        'Casual Encounter',
        'Sales Presentation',
        'Sales Presentation / Demo',
        'Investor Pitch',
        'Objection Handling',
        'Document Defense',
        'Messaging Defense'
    ];

    const now = new Date();
    const currentWeekStart = getWeekStart(now);

    // Build array of week start dates going back N weeks (UTC-based)
    const weeks = [];
    for (let i = numberOfWeeks - 1; i >= 0; i--) {
        const weekStart = new Date(currentWeekStart.getTime() - i * 7 * 24 * 60 * 60 * 1000);
        weeks.push(weekStart);
    }

    // Filter valid completed assessments
    const completed = assessments.filter(
        a => a.completedDateTime && validSubmissionTypes.includes(a.sessionType)
    );

    // Map session types for grouping (merge Sales Presentation variants)
    const normalizeType = (type) => {
        if (type === 'Sales Presentation / Demo') return 'Sales Presentation';
        return type;
    };

    return weeks.map(weekStart => {
        const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
        const weekKey = weekStart.toISOString().split('T')[0];

        const weekAssessments = completed.filter(a => {
            const d = new Date(a.completedDateTime);
            return d >= weekStart && d < weekEnd;
        });

        const month = weekStart.toLocaleDateString('en-US', { month: 'short' });
        const day = weekStart.getUTCDate();

        return {
            week: `${month} ${day}`,
            weekKey,
            'Elevator Pitch': weekAssessments.filter(a => normalizeType(a.sessionType) === 'Elevator Pitch').length,
            'Casual Encounter': weekAssessments.filter(a => normalizeType(a.sessionType) === 'Casual Encounter').length,
            'Sales Presentation': weekAssessments.filter(a => normalizeType(a.sessionType) === 'Sales Presentation').length,
            'Objection Handling': weekAssessments.filter(a => a.sessionType === 'Objection Handling').length,
            'Document Defense': weekAssessments.filter(a => a.sessionType === 'Document Defense').length,
            'Messaging Defense': weekAssessments.filter(a => a.sessionType === 'Messaging Defense').length,
            'Investor Pitch': weekAssessments.filter(a => a.sessionType === 'Investor Pitch').length,
            total: weekAssessments.length
        };
    });
};